import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { CognitoUser } from "amazon-cognito-identity-js";

import { authErrorKey, completeNewPassword, signIn } from "@/auth/cognito/client";
import { useSession } from "@/auth/session/SessionProvider";

import { AuthLayout } from "./AuthLayout";
import { Field, FormError, FormNotice, GoogleButton, PasswordField, SubmitButton } from "./fields";

/**
 * Sign in with email + password, on our own origin.
 *
 * <p>Authentication is SRP (see {@code auth/cognito/client}), so the password
 * never leaves the browser. On success the resulting session is handed to the
 * session facade and the user is returned to wherever they were headed —
 * {@code AuthGate} parks that path in router state when it sends them here.</p>
 *
 * <p>Admin-created users arrive holding a temporary password and get a
 * {@code NEW_PASSWORD_REQUIRED} challenge instead of a session; that's the
 * second phase below rather than a separate route, because it is only ever
 * reachable mid-attempt and has nothing to show on its own.</p>
 */
export function SignInPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const session = useSession();

  const [email, setEmail] = useState(params.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<CognitoUser | null>(null);
  const [newPassword, setNewPassword] = useState("");

  // Where to land after signing in: the deep link AuthGate stashed, else home.
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  // One-shot notices handed over by the other pages via query param, so a
  // reload doesn't keep re-announcing them.
  const notice = params.get("confirmed")
    ? t("auth.notices.confirmed")
    : params.get("reset")
      ? t("auth.notices.passwordReset")
      : null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await signIn(email, password);
      if (result.status === "NEW_PASSWORD_REQUIRED") {
        setChallenge(result.user);
        return;
      }
      session.adoptCognitoSession(result.session);
      navigate(from, { replace: true });
    } catch (err) {
      const code = (err as { code?: string }).code ?? "UnknownError";
      // An unconfirmed account isn't a failure, it's an unfinished sign-up —
      // send them to the step they never completed rather than to a dead end.
      if (code === "UserNotConfirmedException") {
        navigate(`/signup/confirm?email=${encodeURIComponent(email.trim())}`);
        return;
      }
      setError(t(authErrorKey(code)));
    } finally {
      setBusy(false);
    }
  }

  async function onSetNewPassword(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      const cognitoSession = await completeNewPassword(challenge, newPassword);
      session.adoptCognitoSession(cognitoSession);
      navigate(from, { replace: true });
    } catch (err) {
      setError(t(authErrorKey((err as { code?: string }).code ?? "UnknownError")));
    } finally {
      setBusy(false);
    }
  }

  if (challenge) {
    return (
      <AuthLayout title={t("auth.newPassword.title")} subtitle={t("auth.newPassword.subtitle")}>
        <form className="space-y-4" onSubmit={onSetNewPassword}>
          <PasswordField
            label={t("auth.fields.newPassword")}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
          />
          <p className="text-xs text-slate-500">{t("auth.passwordRules")}</p>
          <FormError>{error}</FormError>
          <SubmitButton busy={busy}>{t("auth.newPassword.submit")}</SubmitButton>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t("auth.signIn.title")}
      footer={
        <>
          {t("auth.signIn.noAccount")}{" "}
          <Link to="/signup" className="font-medium text-teal-700 hover:text-teal-800">
            {t("auth.signIn.createAccount")}
          </Link>
        </>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <FormNotice>{notice}</FormNotice>
        <Field
          label={t("auth.fields.email")}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          autoFocus
          required
        />
        <PasswordField
          label={t("auth.fields.password")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
          hint={
            <Link
              to={`/forgot${email ? `?email=${encodeURIComponent(email.trim())}` : ""}`}
              className="text-xs font-medium text-teal-700 hover:text-teal-800"
            >
              {t("auth.signIn.forgot")}
            </Link>
          }
        />
        <FormError>{error}</FormError>
        <SubmitButton busy={busy}>{t("auth.signIn.submit")}</SubmitButton>
      </form>
      <GoogleButton />
    </AuthLayout>
  );
}
